import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { NgbCarousel, NgbSlideEvent } from '@ng-bootstrap/ng-bootstrap';
import { SmartTagColour, SmartTagOrderItem, SmartTagOrderType, SmartTagSize, SmartTagType } from 'src/app/interfaces/smart-tag';

interface TagConfig {
  hook: SmartTagType;
  size: SmartTagSize;
  color: SmartTagColour;
}

interface TagSlide {
  id: string;
  src: string;
  alt: string;
  color: SmartTagColour;
  hook: SmartTagType;
}

/** Carousel images in display order */
const TAG_SLIDES: TagSlide[] = [
  {
    id: 'biscuit-front',
    src: '/assets/images/identibase/smartTag/biscuit-smart-tag-front.webp',
    alt: 'Biscuit smart tag, front',
    color: SmartTagColour.Biscuit,
    hook: SmartTagType.Circular,
  },
  {
    id: 'biscuit-back',
    src: '/assets/images/identibase/smartTag/biscuit-smart-tag-back.webp',
    alt: 'Biscuit smart tag, back',
    color: SmartTagColour.Biscuit,
    hook: SmartTagType.Circular,
  },
  {
    id: 'pink-front',
    src: '/assets/images/identibase/smartTag/pink-smart-tag-front.webp',
    alt: 'Paw pad pink smart tag, front',
    color: SmartTagColour.PawPadPink,
    hook: SmartTagType.Circular,
  },
  {
    id: 'pink-back',
    src: '/assets/images/identibase/smartTag/pink-smart-tag-back.webp',
    alt: 'Paw pad pink smart tag, back',
    color: SmartTagColour.PawPadPink,
    hook: SmartTagType.Circular,
  },
  {
    id: 'indigo-front',
    src: '/assets/images/identibase/smartTag/indigo-smart-tag-front.webp',
    alt: 'Identi indigo smart tag, front',
    color: SmartTagColour.Indigo,
    hook: SmartTagType.Circular,
  },
  {
    id: 'indigo-back',
    src: '/assets/images/identibase/smartTag/indigo-smart-tag-back.webp',
    alt: 'Identi indigo smart tag, back',
    color: SmartTagColour.Indigo,
    hook: SmartTagType.Circular,
  },
  {
    id: 'biscuit-silicone',
    src: '/assets/images/identibase/smartTag/biscuit-silicone-tag.webp',
    alt: 'Biscuit silicone smart tag',
    color: SmartTagColour.Biscuit,
    hook: SmartTagType.HookAndLoop,
  },
  {
    id: 'pink-silicone',
    src: '/assets/images/identibase/smartTag/pink-silicone-tag.webp',
    alt: 'Paw pad pink silicone smart tag',
    color: SmartTagColour.PawPadPink,
    hook: SmartTagType.HookAndLoop,
  },
  {
    id: 'indigo-silicone',
    src: '/assets/images/identibase/smartTag/indigo-silicone-tag.webp',
    alt: 'Identi indigo silicone smart tag',
    color: SmartTagColour.Indigo,
    hook: SmartTagType.HookAndLoop,
  },
];

/** Which slide to show for each color, per hook type */
const SLIDE_FOR_COLOUR: Record<SmartTagColour, { circular: string; silicone: string }> = {
  [SmartTagColour.Indigo]: { circular: 'indigo-front', silicone: 'indigo-silicone' },
  [SmartTagColour.PawPadPink]: { circular: 'pink-front', silicone: 'pink-silicone' },
  [SmartTagColour.Biscuit]: { circular: 'biscuit-front', silicone: 'biscuit-silicone' },
};

/** Dimensions shown on the images, per hook type and size */
const TAG_DIMENSIONS: Record<SmartTagType, Partial<Record<SmartTagSize, string>>> = {
  [SmartTagType.HookAndLoop]: {
    [SmartTagSize.Small]: '(L) 75mm x (W) 20mm x (D) 5mm',
    [SmartTagSize.Medium]: '(L) 78mm x (W) 37mm x (D) 5mm',
  },
  [SmartTagType.Circular]: {
    [SmartTagSize.Small]: '25mm wide',
    [SmartTagSize.Medium]: '30mm wide',
  },
};

/** Tags that cannot be ordered until they are back in stock */
const OUT_OF_STOCK: TagConfig[] = [];

/** Swatch color of an out of stock tag */
const OUT_OF_STOCK_COLOR = 'var(--color-silver)';

const DEFAULT_CONFIG: TagConfig = {
  hook: SmartTagType.HookAndLoop,
  size: SmartTagSize.Small,
  color: SmartTagColour.Biscuit
};

/** Which CTA buttons to show. */
export type CtaMode =
  | 'all-free'    // all tags are free, one CTA
  | 'mixed'       // some free, some paid, two CTAs
  | 'paid-only';  // no free tags left, one CTA

/** Lets the user pick tags. It does not order or pay, the parent page does that. */
@Component({
  selector: 'app-smart-tag-configurator',
  templateUrl: './smart-tag-configurator.component.html',
  styleUrl: './smart-tag-configurator.component.scss'
})
export class SmartTagConfiguratorComponent implements OnInit {

  /** Pet name in the title. Defaults to 'your pet'. */
  @Input() petName = 'your pet';
  /** How many free tags the keeper has left. */
  @Input() freeTags: number | null = null;
  /** Price of one paid tag. */
  @Input() tagUnitPrice = 0;
  /** Set to false if the page already has a title. */
  @Input() showIntro = true;
  /** Subtitle under the title. Empty string hides it. */
  @Input() subtitle = '';
  /** Set to false to hide the quantity selector. Tags can still be added below the options. */
  @Input() showQuantitySelector = true;
  /** Set to false to hide the free/paid badge while there is only one tag. The accordion keeps its badges. */
  @Input() showSingleItemBadge = true;

  /** Text of a link under the CTA for proceeding without any tag. Empty string hides the link. */
  @Input() skipCtaText = '';

  @Output() continueFree = new EventEmitter<SmartTagOrderItem[]>();
  @Output() continueWithPayment = new EventEmitter<SmartTagOrderItem[]>();
  @Output() skipTags = new EventEmitter<void>();

  readonly maxQuantity = 10;
  quantity = 1;
  items: TagConfig[] = [{ ...DEFAULT_CONFIG }];

  readonly colors = [
    SmartTagColour.Biscuit,
    SmartTagColour.PawPadPink,
    SmartTagColour.Indigo,
  ];
  
  readonly colorLabels: Record<SmartTagColour, string> = {
    [SmartTagColour.Indigo]: 'Identi indigo',
    [SmartTagColour.PawPadPink]: 'Paw pad pink',
    [SmartTagColour.Biscuit]: 'Biscuit'
  };

  readonly colorHex: Record<SmartTagColour, string> = {
    [SmartTagColour.Indigo]: '#2E38B2',
    [SmartTagColour.PawPadPink]: '#FFA99B',
    [SmartTagColour.Biscuit]: '#FFE4B0'
  };

  readonly hookLabels: Record<SmartTagType, string> = {
    [SmartTagType.HookAndLoop]: 'Hook & Loop',
    [SmartTagType.Circular]: 'Circular'
  };

  readonly SmartTagType = SmartTagType;
  readonly SmartTagSize = SmartTagSize;

  /** The tag the images belong to */
  public activeItemIndex = 0;
  /** Images of the hook type the open tag has. */
  public slides: TagSlide[] = [];
  /** The first slide to show. */
  public initialSlideId = '';
  /** The slide the user navigating to. Null when the carousel already shows it. */
  private pendingSlideId: string | null = null;
  private outOfStock = OUT_OF_STOCK;

  @ViewChild(NgbCarousel) private carousel?: NgbCarousel;

  ngOnInit(): void {
    this.slides = this.slidesFor(this.activeItem.hook);
    this.initialSlideId = this.slideIdForSelection();
  }

  // Computed helpers

  get showAccordion(): boolean {
    return this.quantity > 1;
  }

  /** The tag the images belong to. */
  private get activeItem(): TagConfig {
    return this.items[this.activeItemIndex] ?? this.items[0];
  }

  /** Locked until tag allowance is known.
   * Without it no badge or CTA can tell free and paid tags apart. */
  get locked(): boolean {
    return this.freeTags === null;
  }

  private get freeQuota(): number {
    return this.freeTags ?? 0;
  }

  // Free tags come first in the list.
  isTagIncluded(index: number): boolean {
    return index < this.freeQuota;
  }

  get freeCount(): number {
    return Math.min(this.quantity, this.freeQuota);
  }

  get paidCount(): number {
    return Math.max(0, this.quantity - this.freeQuota);
  }

  get totalPaidPrice(): string {
    const total = this.paidCount * this.tagUnitPrice;
    return `£${total.toFixed(2)}`;
  }

  get ctaMode(): CtaMode {
    if (this.freeCount === 0) return 'paid-only';
    if (this.paidCount === 0) return 'all-free';
    return 'mixed';
  }

  /** Blocks every CTA while any picked tag is out of stock. */
  get hasOutOfStockItem(): boolean {
    return this.items.some(item => this.isOutOfStock(item));
  }

  /** Whether the tag is out of stock, in its own color or the one given. */
  isOutOfStock(config: TagConfig, color = config.color): boolean {
    return this.outOfStock.some(entry =>
      entry.hook === config.hook && entry.size === config.size && entry.color === color
    );
  }

  // Carousel

  /** Jumps to the image of the picked tag.
   * The other images of the same hook type stay in the carousel. */
  private updateSlides(): void {
    const target = this.slideIdForSelection();
    if (!target) return;

    const hook = this.activeItem.hook;

    // in case of selecting a different tag style
    // change image list and navigate to the picked tag
    if (this.slides[0].hook !== hook) {
      this.slides = this.slidesFor(hook);
      this.pendingSlideId = null;
      if (this.carousel) {
        this.carousel.activeId = target;
      }
      return;
    }

    // activeId is where the carousel is going, not where it is now. Also skip a slide we are already moving to.
    if (target === (this.carousel?.activeId ?? this.initialSlideId)) {
      this.pendingSlideId = null;
      return;
    }

    this.pendingSlideId = target;
    this.carousel?.select(target);
  }

  /** Shows proper image for tag types */
  private slideIdForSelection(): string {
    const item = this.activeItem;
    if (!item) return '';

    const target = SLIDE_FOR_COLOUR[item.color];
    return item.hook === SmartTagType.HookAndLoop ? target.silicone : target.circular;
  }

  private slidesFor(hook: SmartTagType): TagSlide[] {
    return TAG_SLIDES.filter(slide => slide.hook === hook);
  }

  /** select() is ignored mid-slide, so retry once the slide finishes. */
  onSlid(event: NgbSlideEvent): void {
    if (!this.pendingSlideId) return;

    if (event.current === this.pendingSlideId) {
      this.pendingSlideId = null;
      return;
    }

    this.carousel?.select(this.pendingSlideId);
  }

  selectHook(item: TagConfig, hook: SmartTagType): void {
    item.hook = hook;
    this.updateSlides();
  }

  selectColor(item: TagConfig, color: SmartTagColour): void {
    item.color = color;
    this.updateSlides();
  }

  // Show the images of the tag the user is editing.
  onItemShown(index: number): void {
    this.activeItemIndex = index;
    this.updateSlides();
  }

  // Event handlers

  /** Removes one tag from the list. */
  removeItem(index: number, event: Event): void {
    event.stopPropagation(); // avoid interaction with the accordion header
    if (this.locked || this.items.length <= 1) return; // "remove" is a link, so it needs its own guard

    this.items.splice(index, 1);
    this.quantity = this.items.length;
    // The open panel keeps its tag, so the index of that tag has to follow the splice.
    if (index < this.activeItemIndex) this.activeItemIndex--;
    this.activeItemIndex = Math.min(this.activeItemIndex, this.items.length - 1);
    this.updateSlides();
  }

  onQuantityChange(value: number): void {
    const newQty = Math.min(this.maxQuantity, Math.max(1, value));
    const diff = newQty - this.items.length;

    if (diff > 0) {
      for (let i = 0; i < diff; i++) {
        this.items.push({ ...DEFAULT_CONFIG });
      }
    } else if (diff < 0) {
      this.items.splice(newQty);
    }

    this.quantity = newQty;
    this.activeItemIndex = Math.min(this.activeItemIndex, this.items.length - 1);
    this.updateSlides();
  }

  // User continues with the free tags only.
  onContinueFreeOnly(): void {
    this.continueFree.emit(this.freeItems());
  }

  // User continues with all tags and pays for the extra ones.
  onContinueWithPayment(): void {
    this.continueWithPayment.emit([...this.freeItems(), ...this.paidItems()]);
  }

  private freeItems(): SmartTagOrderItem[] {
    return this.toOrderItems(this.items.slice(0, this.freeQuota), SmartTagOrderType.FreeNew);
  }

  private paidItems(): SmartTagOrderItem[] {
    return this.toOrderItems(this.items.slice(this.freeQuota), SmartTagOrderType.PurchaseNew);
  }

  private toOrderItems(items: TagConfig[], orderType: SmartTagOrderType): SmartTagOrderItem[] {
    return items.map(item => ({ size: item.size, colour: item.color, type: item.hook, orderType }));
  }

  // Display helpers

  /** Dimensions of the tag on the slide, in the size the user picked. */
  getDimensions(slide: TagSlide): string {
    return TAG_DIMENSIONS[slide.hook][this.activeItem.size] ?? '';
  }

  /** Whether the tag on the slide is out of stock in the size the user picked. */
  isSlideOutOfStock(slide: TagSlide): boolean {
    return this.isOutOfStock({ hook: slide.hook, size: this.activeItem.size, color: slide.color });
  }

  /** Color of the swatch, grey when the tag would be out of stock in that color. */
  getColorHex(item: TagConfig, color = item.color): string {
    return this.isOutOfStock(item, color) ? OUT_OF_STOCK_COLOR : this.colorHex[color];
  }

  getAccordionHeaderText(item: TagConfig): string {
    return `${this.hookLabels[item.hook]} - ${item.size === SmartTagSize.Small ? 'S' : 'M'}`;
  }

  getColorLabel(color: SmartTagColour): string {
    return this.colorLabels[color];
  }
}
